import { useAuthStore, useConnectionStore, formatTime } from '@sentinel/web-core';
import { StateBadge, type StateTone } from '@sentinel/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentSpace } from './nav/use-current-space';
import { StationService } from './StationService';

const LINK_TONE: Record<'online' | 'degraded' | 'offline', StateTone> = {
  online: 'ok',
  degraded: 'att',
  offline: 'crit',
};

function useClock(): string {
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date().toISOString()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Connection, last sync, the services and the link of this station: always visible at the
 *  bottom (CLAUDE.md §20, §32), so the topbar keeps only what one acts on (proposal 11). */
export function StatusBar() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const link = useConnectionStore((s) => s.link());
  const online = useConnectionStore((s) => s.browserOnline);
  const realtime = useConnectionStore((s) => s.realtime);
  const latency = useConnectionStore((s) => s.latencyMs);
  const lastSync = useConnectionStore((s) => s.lastSyncAt);
  const now = useClock();
  const { current } = useCurrentSpace();
  const core = online
    ? latency === null
      ? t('shell.status.up')
      : t('shell.status.upLatency', { ms: latency })
    : t('shell.status.down');

  return (
    <footer className="statusbar">
      <span className="u">
        {t('shell.status.connection')}
        <StateBadge tone={LINK_TONE[link]} label={t(`shell.connection.${link}`)} />
      </span>
      <span className="u">
        {t('shell.status.sync')}
        <span className="mono">
          {lastSync === null ? t('shell.status.never') : formatTime(lastSync, true)}
        </span>
      </span>
      <span className="u">
        {t('shell.status.services')} <b>{t('shell.nav.serviceCore')}</b>
        <StateBadge tone={online ? 'ok' : 'crit'} label={core} />
        <b>{t('shell.nav.serviceRealtime')}</b>
        <StateBadge
          tone={realtime === 'connected' ? 'ok' : 'att'}
          label={realtime === 'connected' ? t('shell.status.up') : t('shell.status.reconnecting')}
        />
      </span>
      <StationService />
      <span className="u push">
        {me === null || me.organization === null
          ? null
          : [me.organization.name, current === null ? null : t(`shell.spaces.${current.key}`), '']
              .filter((part) => part !== null)
              .join(' · ')}
        <span className="mono">{formatTime(now, true)}</span>
      </span>
    </footer>
  );
}
